import { useEffect, useState } from "react";

import AddressDisplay from "~/components/address/AddressDisplay.tsx";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import type { Address } from "~/types/addressTypes.ts";

interface Props {
	address: Address;
	width?: number;
	height?: number;
}

const MapDisplay: React.FC<Props> = ({ address, width = 600, height = 360 }) => {
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [mapsUrl, setMapsUrl] = useState<string | null>(null);
	const { street, houseNumber, zipCode, city, countryId } = address;
	const fullAddress = `${street} ${houseNumber}, ${zipCode} ${city}`;

	useEffect(() => {
		const fetchImage = async () => {
			const accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;

			const geocodingResponse = await fetch(
				`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
					fullAddress,
				)}.json?access_token=${accessToken}`,
			);
			const geocodingData = await geocodingResponse.json();
			const coordinates = geocodingData.features[0].center; // [longitude, latitude]
			const encodedAddress = encodeURIComponent(fullAddress);

			const staticImageUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l(${coordinates[0]},${coordinates[1]})/${coordinates[0]},${coordinates[1]},8,0,0/${width}x${height}?access_token=${accessToken}`;
			setImageUrl(staticImageUrl);
			setMapsUrl(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`);
		};

		fetchImage();
	}, [address, fullAddress, height, width]);

	return (
		<div className="flex items-start justify-start gap-4">
			<AddressDisplay city={city}
							houseNumber={houseNumber}
							street={street}
							zipCode={zipCode}
							countryId={countryId} />
			<a href={mapsUrl!}
			   target="_blank"
			   className="flex w-80 grow items-center justify-center overflow-hidden rounded bg-gray-500"
			   rel="noreferrer">
				{imageUrl ? <img src={imageUrl}
								 alt={fullAddress}
								 className="w-full" /> : <LoadingSpinner />}
			</a>
		</div>
	);
};

export default MapDisplay;
